import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './DiceSimulation.css';
import DiceDisplay from './DiceDisplay';
import Chart from './Chart';
import DataTables from './DataTables';
import Controls from './Controls';
import { translations } from '../translations';

const DiceSimulation = ({ language }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [rollCount, setRollCount] = useState(0);
  const [speed, setSpeed] = useState(5);
  const [frequencies, setFrequencies] = useState({});
  const [currentRoll, setCurrentRoll] = useState({ dice4: null, dice6: null, sum: null });
  
  const maxRolls = 1000;
  const intervalRef = useRef(null);
  const t = translations[language];
  
  // 4-sided die faces (3 numbers on each face, top one is valid)
  const dice4Faces = useMemo(() => [
    [1, 2, 3], // Face 1: top is 1
    [2, 3, 4], // Face 2: top is 2
    [3, 4, 1], // Face 3: top is 3
    [4, 1, 2]  // Face 4: top is 4
  ], []);

  // Initialize frequencies
  useEffect(() => {
    const initialFrequencies = {};
    for (let i = 2; i <= 10; i++) {
      initialFrequencies[i] = 0;
    }
    setFrequencies(initialFrequencies);
  }, []);

  const rollDice4 = useCallback(() => {
    const faceIndex = Math.floor(Math.random() * 4);
    const face = dice4Faces[faceIndex];
    return face[0]; // Get the top number
  }, [dice4Faces]);

  const rollDice6 = useCallback(() => {
    return Math.floor(Math.random() * 6) + 1;
  }, []);

  const rollDice = useCallback(() => {
    const dice4Value = rollDice4();
    const dice6Value = rollDice6();
    const sum = dice4Value + dice6Value;

    setCurrentRoll({ dice4: dice4Value, dice6: dice6Value, sum });

    setFrequencies(prev => {
      const newFrequencies = { ...prev };
      if (sum >= 2 && sum <= 10) {
        newFrequencies[sum] = (newFrequencies[sum] || 0) + 1;
      }
      return newFrequencies;
    });

    setRollCount(prev => prev + 1);
  }, [rollDice4, rollDice6]);

  const startSimulation = useCallback(() => {
    if (isRunning) return;
    
    setIsRunning(true);
    const intervalTime = 1100 - (speed * 100); // 100ms - 1000ms range
    
    intervalRef.current = setInterval(() => {
      rollDice();
    }, intervalTime);
  }, [isRunning, speed, rollDice]);

  const stopSimulation = useCallback(() => {
    if (!isRunning) return;
    
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isRunning]);

  const resetSimulation = useCallback(() => {
    stopSimulation();
    setRollCount(0);
    setCurrentRoll({ dice4: null, dice6: null, sum: null });
    
    const initialFrequencies = {};
    for (let i = 2; i <= 10; i++) {
      initialFrequencies[i] = 0;
    }
    setFrequencies(initialFrequencies);
  }, [stopSimulation]);

  const handleSpeedChange = useCallback((newSpeed) => {
    setSpeed(newSpeed);
    if (isRunning) {
      stopSimulation();
      setTimeout(() => {
        startSimulation();
      }, 100);
    }
  }, [isRunning, stopSimulation, startSimulation]);

  // Stop when maximum roll count is reached
  useEffect(() => {
    if (rollCount >= maxRolls) {
      stopSimulation();
    }
  }, [rollCount, stopSimulation]);

  // Clear interval when component unmounts
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <div className="dice-simulation">
      <header>
        <h1>{t.title}</h1>
      </header>

      <div className="problem-statement">
        <p>
          <strong>Problem:</strong> {t.problemStatement}
        </p>
      </div>

      <Controls
        isRunning={isRunning}
        speed={speed}
        onStart={startSimulation}
        onStop={stopSimulation}
        onReset={resetSimulation}
        onSpeedChange={handleSpeedChange}
        language={language}
      />

      <DiceDisplay currentRoll={currentRoll} language={language} />

      <div className="stats">
        <div className="stat-item">
          <span className="stat-label">{t.rollCount}</span>
          <span className="stat-value">{rollCount}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">{t.maxRolls}</span>
          <span className="stat-value">{maxRolls}</span>
        </div>
      </div>

      <Chart frequencies={frequencies} language={language} />

      <DataTables frequencies={frequencies} language={language} />
    </div>
  );
};

export default DiceSimulation; 